import React from 'react';
import { Globe, Code2, Sparkles, Check, ArrowUpRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { servicesData } from '../data/mock';

const Services = () => {
  const icons = [Globe, Code2, Sparkles];

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-28 lg:py-36"
      style={{ backgroundColor: '#0b1120' }}
    >
      {/* Decorative */}
      <div className="absolute top-40 left-1/3 w-[420px] h-[420px] rounded-full bg-violet-600/10 blur-[140px] pointer-events-none" />
      <div className="absolute inset-0 bg-grid bg-grid-fade opacity-40 pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-2xl mb-16 lg:mb-20">
          <span className="inline-block text-xs font-bold uppercase tracking-[0.22em] text-violet-300 mb-5" data-testid="services-eyebrow">
            Szolgáltatások
          </span>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-[3.5rem] font-semibold text-white leading-[1.05] tracking-tight text-balance" data-testid="services-title">
            Mindent egy kézből,
            <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(120deg, #a78bfa, #e879f9)' }}> növekedésre tervezve</span>
          </h2>
          <p className="mt-5 text-lg text-slate-400 max-w-xl">
            Teljes körű digitális megoldások, amelyek valódi ügyfeleket és mérhető eredményt hoznak.
          </p>
        </div>

        {/* Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7">
          {servicesData.map((service, index) => {
            const Icon = icons[index];
            const offset = index === 1 ? 'lg:mt-12' : '';
            return (
              <motion.article
                key={service.id}
                data-testid={`service-card-${service.id}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.8, delay: index * 0.12, ease: [0.22, 1, 0.36, 1] }}
                className={`group relative ${offset} spotlight rounded-[2rem] bg-gradient-to-b from-slate-50 to-slate-100 p-8 lg:p-10 ring-1 ring-slate-900/5
                  shadow-[0_20px_50px_-20px_rgba(15,23,42,0.6)] hover:-translate-y-1.5 transition-all duration-500 overflow-hidden`}
              >
                {/* Accent corner glow */}
                <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-gradient-to-br from-violet-400/30 to-fuchsia-400/30 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

                <div className="relative flex items-start justify-between">
                  <div className="relative">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-600 to-fuchsia-500 flex items-center justify-center shadow-lg shadow-violet-500/30">
                      <Icon className="w-7 h-7 text-white" strokeWidth={2.2} />
                    </div>
                  </div>
                  <span className="text-xs font-semibold tracking-widest uppercase text-slate-400">
                    0{service.id}
                  </span>
                </div>

                <h3 className="font-display text-2xl lg:text-3xl font-semibold text-slate-900 tracking-tight mt-7 mb-3">
                  {service.title}
                </h3>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-7 max-w-lg">
                  {service.description}
                </p>

                <ul className="space-y-3 mb-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <span className="mt-0.5 w-5 h-5 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0 ring-1 ring-violet-200">
                        <Check className="w-3 h-3 text-violet-700" strokeWidth={3} />
                      </span>
                      <span className="text-slate-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                {service.image && (
                  <div className="mt-8 relative overflow-hidden rounded-2xl ring-1 ring-slate-900/10">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-52 object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 via-transparent to-transparent" />
                  </div>
                )}

                <div className="mt-8 pt-6 border-t border-slate-200/70 flex items-center justify-between">
                  <span className="text-sm text-slate-500">Kérj ajánlatot erre a szolgáltatásra</span>
                  <span
                    className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-slate-900 text-white transition-all duration-300 group-hover:bg-violet-600 group-hover:rotate-[-20deg]"
                    aria-hidden="true"
                  >
                    <ArrowUpRight className="w-4 h-4" />
                  </span>
                </div>
              </motion.article>
            );
          })}
        </div>

        {/* Partner note */}
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          data-testid="services-partner-note"
          className="mt-12 lg:mt-16 max-w-2xl text-sm lg:text-base text-slate-400 leading-relaxed"
        >
          <Sparkles className="inline-block w-3.5 h-3.5 text-fuchsia-300 mr-2 -mt-0.5" />
          AI-fejlesztési partnereinkkel komplexebb automatizálási és döntéstámogatási megoldásokat is megvalósítunk.
        </motion.p>
      </div>
    </section>
  );
};

export default Services;
